

import React from "react";
import styled, { keyframes } from "styled-components";
import ModalWrapper from "./ModalWrapper";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  showCloseIcon?: boolean;
  closeOnOverlayClick?: boolean;
  children: JSX.Element;
}

// Fade-in animation
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Backdrop for modal
const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

// Modal container
const ModalContainer = styled.div`
  width: 90%;
  max-width: 580px;
  background: #fff;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 80vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 1025px) {
    padding: 2rem;
  }
`;

const CustomModal: React.FC<ModalProps> = ({
  open,
  onClose,
  showCloseIcon = true,
  closeOnOverlayClick = true,
  children,
}) => {
  if (!open) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Backdrop onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalWrapper onClose={onClose}>{children}</ModalWrapper>
      </ModalContainer>
    </Backdrop>
  );
};

interface ModalVariantsProps {
  open: boolean;
  setOpen?: (state: boolean) => void;
  closeModal?: () => void;
  openModal?: () => void;

  children: JSX.Element;
  closeOnOverlayClick?: boolean;
  showCloseIcon?: boolean;
}

const SmallModal: React.FC<ModalVariantsProps> = (props) => (
  <ModalVariant {...props} maxHeight="80vh" />
);
const MediumModal: React.FC<ModalVariantsProps> = (props) => (
  <ModalVariant {...props} maxHeight="80vh" />
);
const LargeModal: React.FC<ModalVariantsProps> = (props) => (
  <ModalVariant {...props} maxHeight="80vh" />
);
const ToastModal: React.FC<ModalVariantsProps> = (props) => (
  <ModalVariant {...props} maxHeight="auto" maxWidth="90%" padding="0" />
);

interface ModalVariantProps extends ModalVariantsProps {
  maxHeight?: string;
  maxWidth?: string;
  padding?: string;
}

const ModalVariant: React.FC<ModalVariantProps> = ({
  open,
  setOpen,
  closeModal,
  children,
  closeOnOverlayClick = true,
  showCloseIcon = true,
}) => {
  const handleCloseModal = () => {
    if (setOpen) {
      setOpen(false);
    } else if (closeModal) {
      closeModal();
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={handleCloseModal}
      showCloseIcon={showCloseIcon}
      closeOnOverlayClick={closeOnOverlayClick}
    >
      {children}
    </CustomModal>
  );
};

export { SmallModal, MediumModal, LargeModal, ToastModal };
